import { formValueString, toMultiValue } from './product-form-values.js';
import { PDLC_DELIVERABLE_KEYS } from './pdlc-deliverables.js';
import { isOnPriceList } from './price-list.js';
import { SAGE_COLUMN_PREFIX, SAGE_FIELDS } from './sage.types.js';
/** Filter key for the price-list toggle: a predicate over two fields, not a column match. */
export const PRICE_LIST_FILTER_KEY = '__onPriceList';
/** Filter key for "Sage only": a column switch, never a row filter. */
export const SAGE_ONLY_FILTER_KEY = '__sageOnly';
/** PDLC deliverable columns: Asana tasks, not metadata fields (#809). */
export const PDLC_COLUMN_KEYS = new Set(PDLC_DELIVERABLE_KEYS);
/** Built-in column keys the grid renders as currency (`$…`). */
export const CURRENCY_KEYS = new Set([
    'euCost', 'targetCost', 'usActualCost', 'euActualCost', 'targetDealer', 'targetMAP', 'tariffDiscountCost',
    'euDealerPrice', 'euMapPrice', 'euListPrice', 'usDealerPrice', 'usMapPrice', 'usListPrice',
    'pro1stDiscountLevel', 'pro2ndDiscountLevel', 'pro3rdDiscountLevel',
    // vendor_cost is NOT here: it may be GBP, so it renders as a plain number.
    ...SAGE_FIELDS.filter(f => f.type === 'money').map(f => `${SAGE_COLUMN_PREFIX}${f.key}`),
]);
export const getFieldValue = (product, field, dynamicFields, userNameById) => {
    const md = product.metadata;
    // PDLC deliverable: 'Done' / 'Open', '' when there is no task or no project.
    // Not 'Not done' — the filter matches substrings, so 'done' would hit both.
    if (PDLC_COLUMN_KEYS.has(field)) {
        const cell = product.pdlc?.deliverables[field];
        return cell ? (cell.completed ? 'Done' : 'Open') : '';
    }
    if (field.startsWith(SAGE_COLUMN_PREFIX)) {
        const key = field.slice(SAGE_COLUMN_PREFIX.length);
        const v = product.sage?.values[key];
        if (v !== undefined && v !== null)
            return v;
        // -Infinity is the missing-number sentinel, so blanks sort last.
        return SAGE_FIELDS.find(f => f.key === key)?.type === 'text' ? '' : -Infinity;
    }
    switch (field) {
        case 'name':
            return product.metadata.name || '';
        case 'productId':
            return product.productId || '';
        case 'version':
            return product.version;
        case 'productManager': {
            // Stored as a userId (USR-…) after the v4 rebuild — show the display name.
            const pm = product.metadata.productManager || '';
            return (pm && userNameById?.[pm]) || pm;
        }
        case 'productStatus':
            return product.metadata.productStatus || '';
        case 'brand':
            return product.metadata.brand || '';
        case 'type':
            return product.metadata.type || '';
        case 'category':
            return product.metadata.category || '';
        case 'euCost':
            return product.metadata.euCost ?? -Infinity;
        case 'targetCost':
            return product.metadata.targetCost ?? -Infinity;
        case 'usActualCost':
            return product.metadata.usActualCost ?? -Infinity;
        case 'euActualCost':
            return product.metadata.euActualCost ?? -Infinity;
        case 'margin':
            return product.metadata.margin ?? -Infinity;
        case 'targetDealer':
            return product.metadata.targetDealer ?? -Infinity;
        case 'targetMAP':
            return product.metadata.targetMAP ?? -Infinity;
        case 'tariffDiscountCost':
            return product.metadata.tariffDiscountCost ?? -Infinity;
        case 'addToPriceList':
            return md.addToPriceList || '';
        case 'sort':
            return md.sort ?? -Infinity;
        case 'itemNumber':
            return md.itemNumber || '';
        case 'productCategory':
            return md.productCategory || '';
        case 'productSeries':
            return md.productSeries || '';
        case 'priceChange':
            return md.priceChange || '';
        case 'poaOnPriceList':
            return md.poaOnPriceList ? 'Yes' : md.poaOnPriceList === false ? 'No' : '';
        case 'discountStructure':
            return md.discountStructure || '';
        case 'euDealerPrice':
            return md.euDealerPrice ?? -Infinity;
        case 'euMapPrice':
            return md.euMapPrice ?? -Infinity;
        case 'euListPrice':
            return md.euListPrice ?? -Infinity;
        case 'usDealerPrice':
            return md.usDealerPrice ?? -Infinity;
        case 'usMapPrice':
            return md.usMapPrice ?? -Infinity;
        case 'usListPrice':
            return md.usListPrice ?? -Infinity;
        case 'pro1stDiscountLevel':
            return md.pro1stDiscountLevel ?? -Infinity;
        case 'pro2ndDiscountLevel':
            return md.pro2ndDiscountLevel ?? -Infinity;
        case 'pro3rdDiscountLevel':
            return md.pro3rdDiscountLevel ?? -Infinity;
        case 'masterPack':
            return md.masterPack ?? -Infinity;
        case 'outerBoxLengthCm':
            return md.outerBoxLengthCm ?? -Infinity;
        case 'outerBoxWidthCm':
            return md.outerBoxWidthCm ?? -Infinity;
        case 'outerBoxHeightCm':
            return md.outerBoxHeightCm ?? -Infinity;
        case 'grossWeightKg':
            return md.grossWeightKg ?? -Infinity;
        case 'upcCode':
            return md.upcCode || '';
        case 'itemCode':
            return md.itemCode || '';
        case 'sku':
            return md.sku || '';
        case 'updatedAt':
            return product.metadata.updatedAt || '';
        default: {
            // Admin-defined dynamic metadata field. Type the returned value so it
            // sorts/filters/formats consistently with comparable built-in columns:
            //  - numeric types use -Infinity as the "missing" sentinel (like costs)
            //  - boolean resolves to its display label (like poaOnPriceList)
            //  - everything else returns the raw string
            const dynField = dynamicFields?.[field];
            if (dynField) {
                const raw = md[field];
                if (dynField.type === 'number' || dynField.type === 'integer' || dynField.type === 'currency') {
                    return raw ?? -Infinity;
                }
                // One joined string, so sort, filter, search and export treat it as text.
                if (dynField.type === 'multiselect')
                    return formValueString(toMultiValue(raw));
                if (dynField.type === 'boolean') {
                    return raw === true
                        ? (dynField.trueLabel ?? 'Yes')
                        : raw === false
                            ? (dynField.falseLabel ?? 'No')
                            : '';
                }
                return raw ?? '';
            }
            return '';
        }
    }
};
/**
 * Keep the rows matching every column filter: a substring per term, OR within a
 * column, AND across columns. Free-text search is the caller's, because the grid
 * searches its formatted cells.
 */
export function filterProducts(products, filters, dynamicFields, userNameById) {
    const entries = Object.entries(filters);
    if (entries.length === 0)
        return products;
    return products.filter((p) => entries.every(([fieldKey, filterValue]) => {
        // Price-list membership is a predicate over two fields, not a value
        // match on one column, so it cannot go through getFieldValue. "Not on
        // the price list" is mostly products whose addToPriceList is BLANK,
        // which a value filter can never select.
        if (fieldKey === PRICE_LIST_FILTER_KEY) {
            const mode = Array.isArray(filterValue) ? filterValue[0] : filterValue;
            if (mode !== 'on' && mode !== 'off')
                return true;
            return isOnPriceList(p.metadata) === (mode === 'on');
        }
        if (fieldKey === SAGE_ONLY_FILTER_KEY)
            return true;
        const terms = (Array.isArray(filterValue) ? filterValue : [filterValue])
            .map(String)
            .filter((v) => v.trim() !== '');
        if (terms.length === 0)
            return true;
        const fieldValue = getFieldValue(p, fieldKey, dynamicFields, userNameById);
        if (fieldValue === null || fieldValue === undefined || fieldValue === -Infinity)
            return false;
        const fieldStr = String(fieldValue).toLowerCase();
        return terms.some((term) => fieldStr.includes(term.toLowerCase()));
    }));
}
function compareOn(a, b, field, order, getValue) {
    const aVal = getValue(a, field);
    const bVal = getValue(b, field);
    if (typeof aVal === 'string' && typeof bVal === 'string') {
        return order === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return order === 'asc' ? aVal - bVal : bVal - aVal;
}
/**
 * Sort the spreadsheet by the chosen column, breaking ties on product name A–Z
 * (#606) — so "Status, then fixture name" is what you get from one click on
 * Status, the way Smartsheet's two-level sort behaves.
 *
 * The tiebreak is always ascending, whichever way the primary column runs:
 * reversing Status should not also reverse the names inside each status, which
 * is the thing that makes a list hard to scan.
 *
 * A primary comparison of 0 OR NaN falls through to the name — NaN is what the
 * numeric branch produces when a column holds a mix of text and numbers, and
 * ordering those by name beats leaving them in arbitrary order.
 */
export function sortProducts(products, sortField, sortOrder, getValue) {
    return [...products].sort((a, b) => {
        const primary = compareOn(a, b, sortField, sortOrder, getValue);
        if (primary)
            return primary;
        return sortField === 'name' ? 0 : compareOn(a, b, 'name', 'asc', getValue);
    });
}
/** The money columns the sheet writes as `$#,##0.00`; a cost multiplier, `margin`, is a plain `0.00`. */
const DOLLAR_KEYS = ['euCost', 'targetCost', 'usActualCost', 'euActualCost', 'targetDealer', 'targetMAP', 'tariffDiscountCost'];
/**
 * Write rows into an ExcelJS worksheet the caller made, so this module needs no
 * runtime ExcelJS: the browser downloads the workbook, the server files and
 * emails it (#1297). Same columns, same formatting, either way.
 */
export function fillProductsSheet(ws, products, columns, dynamicFields, userNameById) {
    // Set column widths
    ws.columns = columns.map(col => {
        let width;
        switch (col.key) {
            case 'image':
                width = 12;
                break;
            case 'name':
                width = 30;
                break;
            case 'productId':
                width = 15;
                break;
            case 'productManager':
                width = 20;
                break;
            case 'productStatus':
                width = 15;
                break;
            case 'brand':
                width = 15;
                break;
            case 'type':
                width = 15;
                break;
            case 'category':
                width = 15;
                break;
            case 'updatedAt':
                width = 12;
                break;
            default: width = 12;
        }
        return { header: col.label, width };
    });
    // Style header row
    const headerRow = ws.getRow(1);
    headerRow.height = 25;
    headerRow.eachCell(cell => {
        cell.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        cell.border = {
            top: { style: 'thin', color: { argb: 'FF000000' } },
            bottom: { style: 'thin', color: { argb: 'FF000000' } },
            left: { style: 'thin', color: { argb: 'FF000000' } },
            right: { style: 'thin', color: { argb: 'FF000000' } },
        };
    });
    // Add data rows
    for (let i = 0; i < products.length; i++) {
        const product = products[i];
        const rowData = [];
        for (const col of columns) {
            const value = getFieldValue(product, col.key, dynamicFields, userNameById);
            let formattedValue;
            switch (col.key) {
                case 'image':
                    formattedValue = product.metadata.imageFileId ? 'Has Image' : '';
                    break;
                case 'euCost':
                case 'targetCost':
                case 'usActualCost':
                case 'euActualCost':
                case 'targetDealer':
                case 'targetMAP':
                case 'tariffDiscountCost':
                    formattedValue = value === -Infinity || value === undefined || value === null ? '' : value;
                    break;
                case 'margin':
                    // margin is a cost multiplier (e.g. 4.73), not a percentage — export the raw number (#250)
                    formattedValue = value === -Infinity || value === undefined || value === null ? '' : value;
                    break;
                case 'updatedAt':
                    formattedValue = value ? new Date(value) : '';
                    break;
                default:
                    // -Infinity is the "missing number" sentinel used for numeric
                    // (including dynamic numeric) columns — render it as blank.
                    formattedValue = value === null || value === undefined || value === -Infinity ? '' : value;
            }
            rowData.push(formattedValue);
        }
        const dataRow = ws.addRow(rowData);
        const isAltRow = (i + 1) % 2 === 0; // i+1 because header is row 1
        dataRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            const col = columns[colNumber - 1];
            const isNumeric = col && (DOLLAR_KEYS.includes(col.key) || col.key === 'margin');
            cell.alignment = { vertical: 'middle', horizontal: isNumeric ? 'right' : 'left' };
            cell.border = {
                top: { style: 'thin', color: { argb: 'FFD3D3D3' } },
                bottom: { style: 'thin', color: { argb: 'FFD3D3D3' } },
                left: { style: 'thin', color: { argb: 'FFD3D3D3' } },
                right: { style: 'thin', color: { argb: 'FFD3D3D3' } },
            };
            if (col?.key === 'margin') {
                cell.numFmt = '0.00';
            }
            else if (col && DOLLAR_KEYS.includes(col.key)) {
                cell.numFmt = '$#,##0.00';
            }
            else if (col && CURRENCY_KEYS.has(col.key) && col.key.startsWith(SAGE_COLUMN_PREFIX)) {
                cell.numFmt = '$#,##0.00'; // money only; sage.vendor_cost may be GBP so stays a plain number
            }
            else if (col?.key === 'updatedAt') {
                cell.numFmt = 'yyyy-mm-dd';
            }
            if (isAltRow) {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } };
            }
        });
    }
}
//# sourceMappingURL=product-grid.js.map